import {
  Box,
  Button,
  HStack,
  IconButton,
  Stack,
  Text,
  Spinner,
  Center,
  Link,
  VStack,
} from "@chakra-ui/react";
import { Elements } from "@stripe/react-stripe-js";
import { useState, useMemo } from "react";
import { PaymentMethod, Stripe } from "@stripe/stripe-js";
import { BillingForm } from "./BillingForm";
import { CloseIcon, InfoIcon } from "@chakra-ui/icons";
import { BillingStatus } from "./PaymentStatusBadge";
import { formatDate } from "../../utils";
import dayjs from "dayjs";
import { KYDScope } from "@common/interfaces/ICognito";

interface BillingCardProps {
  stripeObject: Stripe | null;
  onBillingSave: (payment_method: PaymentMethod) => Promise<void>;
  isSaving: boolean;
  isLoading?: boolean;
  paymentMethod?: {
    brand: string;
    last4: string;
    auto_pay: boolean;
    next_payment_date: string;
  };
  status?: BillingStatus;
  permissions: KYDScope[];
}

// Custom hook to determine billing permissions
const useBillingPermissions = (permissions: KYDScope[]) => {
  return useMemo(() => {
    const canEditPermissions: KYDScope[] = ["organizations/write"];

    return {
      canEditBilling: canEditPermissions.some((permission) =>
        permissions?.includes(permission)
      ),
    };
  }, [permissions]);
};

// Custom hook to determine component state
const useBillingState = (
  isLoading: boolean,
  paymentMethod: any,
  status: BillingStatus | undefined,
  showBillingForm: boolean,
  canEditBilling: boolean
) => {
  return useMemo(() => {
    if (isLoading) {
      return "loading";
    }

    if (showBillingForm) {
      return "editing";
    }

    const hasPaymentMethod = !!paymentMethod;
    const hasErrorStatus = status && ["CANCELED", "PAST_DUE"].includes(status);

    if (hasPaymentMethod && hasErrorStatus) {
      return "error";
    }

    if (hasPaymentMethod && canEditBilling) {
      return "configured";
    }

    return "empty";
  }, [isLoading, paymentMethod, status, showBillingForm, canEditBilling]);
};

// Component for the warning message
const WarningMessage = () => (
  <Box p={3} borderColor="gray.200" borderTopWidth="1px" py={4} w="full">
    <HStack spacing={4} align="flex-start">
      <InfoIcon color="red.600" boxSize={5} mt={0.5} />
      <Stack
        spacing={2}
        flex={1}
        color="gray.800"
        fontWeight="medium"
        fontSize="sm"
      >
        <VStack spacing={0} flex={1} align="flex-start">
          <Text>Your email and sms marketing services have been paused.</Text>
          <Text>Please update your payment method to re enable them.</Text>
        </VStack>
        <Text>
          <Link color="blue.500" textDecoration="underline">
            Contact support
          </Link>{" "}
          if you need help.
        </Text>
      </Stack>
    </HStack>
  </Box>
);

// Component for the payment method card
const PaymentMethodCard = ({
  paymentMethod,
  canEdit = false,
  isError = false,
  onUpdate,
}: {
  paymentMethod: any;
  isError?: boolean;
  canEdit?: boolean;
  onUpdate: () => void;
}) => (
  <Box
    bg="white"
    p={4}
    borderRadius="md"
    border="1px solid"
    borderColor={isError ? "red.300" : "gray.200"}
    shadow="sm"
    w="full"
  >
    <Stack
      direction={{ base: "column", md: "row" }}
      justify="space-between"
      align={{ base: "start", md: "center" }}
      spacing={3}
    >
      <Stack spacing={1}>
        <Text
          fontSize="sm"
          fontWeight="bold"
          color="gray.700"
          textTransform="capitalize"
        >
          {paymentMethod.brand} (****{paymentMethod.last4})
        </Text>
        {isError ? (
          <Text fontSize="xs" color="red.500" fontWeight="medium">
            Payment method failed
          </Text>
        ) : (
          <HStack spacing={2} color="gray.500" fontSize="xs">
            <Text>
              Next Payment:{" "}
              {dayjs.utc(paymentMethod?.next_payment_date).format("ddd MMM DD")}
            </Text>
          </HStack>
        )}
      </Stack>
      {canEdit ? (
        <Button
          size="sm"
          bg="white"
          color="gray.700"
          border="1px solid"
          borderColor="gray.300"
          shadow="sm"
          _hover={{
            bg: "gray.50",
            borderColor: "gray.400",
          }}
          onClick={onUpdate}
        >
          {isError ? "Update payment method" : "Change"}
        </Button>
      ) : null}
    </Stack>
  </Box>
);

// Loading state component
const LoadingState = () => (
  <Box
    bg="gray.50"
    p={6}
    borderRadius="lg"
    border="1px solid"
    borderColor="gray.200"
    shadow="sm"
    w="full"
  >
    <Center py={8}>
      <Stack spacing={4} align="center">
        <Spinner size="lg" color="blue.500" />
        <Text fontSize="sm" color="gray.500">
          Loading payment information...
        </Text>
      </Stack>
    </Center>
  </Box>
);

// Error state component
const ErrorState = ({
  paymentMethod,
  canEditBilling,
  onUpdate,
}: {
  paymentMethod: any;
  canEditBilling: boolean;
  onUpdate: () => void;
}) => (
  <Stack w="full" spacing={3}>
    <Text fontSize="lg" fontWeight="medium">
      Payment info
    </Text>
    <WarningMessage />
    <PaymentMethodCard
      paymentMethod={paymentMethod}
      isError={true}
      canEdit={canEditBilling}
      onUpdate={onUpdate}
    />
  </Stack>
);

// Configured state component
const ConfiguredState = ({
  paymentMethod,
  canEditBilling,
  onUpdate,
}: {
  paymentMethod: any;
  canEditBilling: boolean;
  onUpdate: () => void;
}) => (
  <Stack w="full" spacing={3}>
    <Text fontSize="lg" fontWeight="medium">
      Payment info
    </Text>
    <PaymentMethodCard
      paymentMethod={paymentMethod}
      canEdit={canEditBilling}
      onUpdate={onUpdate}
    />
  </Stack>
);

// Editing state component
const EditingState = ({
  stripeObject,
  onBillingSave,
  isSaving,
  onClose,
}: {
  stripeObject: Stripe | null;
  onBillingSave: (payment_method: PaymentMethod) => Promise<void>;
  isSaving: boolean;
  onClose: () => void;
}) => (
  <Box
    bg="gray.50"
    p={6}
    borderRadius="lg"
    border="1px solid"
    borderColor="gray.200"
    shadow="sm"
    w="full"
  >
    <HStack justify="space-between" align="center" mb={6}>
      <Stack spacing={1}>
        <Text fontSize="md" fontWeight="bold" color="gray.700">
          Enable email & SMS marketing
        </Text>
        <Text fontSize="sm" color="gray.500">
          Add payment method to get started
        </Text>
      </Stack>
      <IconButton
        aria-label="Close billing form"
        icon={<CloseIcon />}
        size="md"
        bg="white"
        color="black"
        borderRadius="full"
        boxShadow="0 2px 4px rgba(0, 0, 0, 0.1)"
        _hover={{
          bg: "gray.50",
          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
        }}
        onClick={onClose}
      />
    </HStack>

    <Elements
      stripe={stripeObject}
      options={{
        mode: "setup",
        currency: "usd",
        paymentMethodCreation: "manual",
        setupFutureUsage: "off_session",
      }}
    >
      <BillingForm
        onBillingSave={async (payment_method_id) => {
          await onBillingSave(payment_method_id);
          onClose();
        }}
        isSaving={isSaving}
      />
    </Elements>
  </Box>
);

// Empty state component
const EmptyState = ({
  onAddPaymentMethod,
}: {
  onAddPaymentMethod: () => void;
}) => (
  <Box
    bg="gray.50"
    p={6}
    borderRadius="lg"
    border="1px solid"
    borderColor="gray.200"
    shadow="sm"
    w="full"
  >
    <Stack
      direction={{ base: "column", md: "row" }}
      justify="space-between"
      align={{ base: "start", md: "center" }}
      spacing={4}
    >
      <Stack spacing={1}>
        <Text fontSize="md" fontWeight="bold" color="gray.700">
          Enable email & SMS marketing
        </Text>
        <Text fontSize="sm" color="gray.500">
          Add payment method to get started
        </Text>
      </Stack>
      <Button
        bg="white"
        color="gray.700"
        border="1px solid"
        borderColor="gray.300"
        shadow="sm"
        _hover={{
          bg: "gray.50",
          borderColor: "gray.400",
        }}
        onClick={onAddPaymentMethod}
      >
        Add Payment Method
      </Button>
    </Stack>
  </Box>
);

export const BillingCard: React.FC<BillingCardProps> = ({
  stripeObject,
  onBillingSave,
  isSaving,
  isLoading = false,
  paymentMethod,
  permissions,
  status,
}) => {
  const [showBillingForm, setShowBillingForm] = useState(false);

  const { canEditBilling } = useBillingPermissions(permissions);
  const billingState = useBillingState(
    isLoading,
    paymentMethod,
    status,
    showBillingForm,
    canEditBilling
  );

  const handleShowBillingForm = () => setShowBillingForm(true);
  const handleHideBillingForm = () => setShowBillingForm(false);

  // Render based on state
  switch (billingState) {
    case "loading":
      return <LoadingState />;

    case "error":
      return (
        <ErrorState
          paymentMethod={paymentMethod}
          canEditBilling={canEditBilling}
          onUpdate={handleShowBillingForm}
        />
      );

    case "configured":
      return (
        <ConfiguredState
          paymentMethod={paymentMethod}
          canEditBilling={canEditBilling}
          onUpdate={handleShowBillingForm}
        />
      );

    case "editing":
      return (
        <EditingState
          stripeObject={stripeObject}
          onBillingSave={onBillingSave}
          isSaving={isSaving}
          onClose={handleHideBillingForm}
        />
      );

    case "empty":
    default:
      return <EmptyState onAddPaymentMethod={handleShowBillingForm} />;
  }
};
